import { HttpParams } from '@angular/common/http';
import { Component, Inject, OnDestroy, OnInit } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { Router } from '@angular/router';
import { Subscription } from 'rxjs';
import { Client } from '../../../../projects/kiene-core/src/lib/components/client/classes/client';
import {
    PdfViewerDesktopService,
    PdfViewerDialogInput,
} from '../../../../projects/kiene-core/src/lib/components/pdf-viewer/kiene-pdf-viewer/kiene-pdf-viewer-desktop/pdf-viewer-desktop.service';
import { API_BASE_URL_SERVICE } from '../../../../projects/kiene-core/src/lib/kiene-core.config';
import { LocalStorageService } from '../../../../projects/kiene-core/src/lib/services/local-storage/local-storage.service';
import { KieneTableService } from '../../components/kiene-table/kiene-table.service';
import {
    ActionTableColumn,
    ConditionSetting,
    EmailGesendetTableColumn,
    PdfTableColumn,
    TableColumn,
} from '../../components/kiene-table/table-column';
import { TableDescriptor } from '../../components/kiene-table/table-descriptor';
import { Condition, Operator, TableOption } from '../../components/kiene-table/table-option';
import { ConfirmDeleteDialogComponent } from '../../dialogs/confirm-delete-dialog/confirm-delete-dialog.component';
import { MessageService } from '../../../../projects/kiene-core/src/lib/services/message-service/message.service';
import { AnzahlProbenDialogComponent } from './anzahl-proben-dialog/anzahl-proben-dialog.component';
import { Probenbegleitschein } from './probenbegleitschein';

@Component({
    selector: 'kiene-probenbegleitschein',
    templateUrl: './probenbegleitschein.component.html',
    styleUrls: ['./probenbegleitschein.component.scss'],
})
export class ProbenbegleitscheinComponent implements OnInit, OnDestroy {
    tableDescriptor: TableDescriptor = new TableDescriptor();
    currentClient: Client;

    watchClientSubscription: Subscription;

    constructor(
        public dialog: MatDialog,
        private messageService: MessageService,
        private pdfService: PdfViewerDesktopService,
        private tableService: KieneTableService,
        private localStorageService: LocalStorageService,
        private router: Router,
        private api: KieneTableService,
        @Inject(API_BASE_URL_SERVICE) private apiBaseUrl: string,
    ) {}

    ngOnDestroy() {
        this.watchClientSubscription.unsubscribe();
        this.tableService = null;
    }

    ngOnInit() {
        this.watchClientSubscription = this.localStorageService.watchCurrentClient().subscribe(() => {
            this.tableService?.reload();
        });
        this.tableDescriptor.headline = 'Probenbegleitscheine';
        this.tableDescriptor.uniqueIdentifier = 'Probenbegleitscheine';
        this.tableDescriptor.apiUrl = this.apiBaseUrl + 'probenbegleitschein/';
        this.tableDescriptor.route = 'probenbegleitscheine';
        this.tableDescriptor.nameOfIdField = 'probenbegleitschein_id';
        this.tableDescriptor.searchPlaceholder = 'Probenbegleitscheine durchsuchen';

        const befundConditions = [new ConditionSetting([new Condition('status_id', Operator.GLEICH, 82)])];
        this.tableDescriptor.columns.push(
            new ActionTableColumn(
                'befund_erstellen',
                'E',
                'Befund erstellen',
                'fast_forward',
                this.createBefund.bind(this),
                1335,
                1335,
                befundConditions,
            ),
        );

        this.tableDescriptor.columns.push(new TableColumn('probenbegleitschein_id', 'Nr.'));
        this.tableDescriptor.columns.push(new TableColumn('kunde', 'Kunde'));
        this.tableDescriptor.columns.push(new TableColumn('zusatz', 'Zusatz'));
        this.tableDescriptor.columns.push(new TableColumn('gruppe', 'Gruppe'));
        this.tableDescriptor.columns.push(new TableColumn('tierarzt', 'Tierarzt'));
        this.tableDescriptor.columns.push(new TableColumn('probenentnahme', 'Probenentnahme', 'date'));
        this.tableDescriptor.columns.push(new TableColumn('status', 'Status'));
        this.tableDescriptor.columns.push(new PdfTableColumn('pdf_id', 'PDF', this.showPdf.bind(this)));
        this.tableDescriptor.columns.push(new EmailGesendetTableColumn());
        // this.tableDescriptor.columns.push(new GedrucktTableColumn());

        this.tableDescriptor.columns.push(
            new ActionTableColumn(
                'pbs_loeschen',
                'L',
                'Probenbegleitschein löschen',
                'delete',
                this.openDeleteDialog.bind(this),
                2504,
                2504,
                null,
            ),
        );
        this.tableDescriptor.permissionCreate = 2502;
        this.tableDescriptor.permissionView = 2501;
        this.tableDescriptor.skipOptionsTablePadding = true;
        this.tableDescriptor.sendCurrentClient = true;

        this.tableDescriptor.options.push(new TableOption(this.duplicate.bind(this), 'duplizieren', 'file_copy', 2509));

        this.tableDescriptor.initialSortColumn = 'erstellt';
        this.tableDescriptor.initialSortDirection = 'desc';
    }

    createBefund(pbs: Probenbegleitschein) {
        this.dialog.open(AnzahlProbenDialogComponent, { data: pbs });
    }

    gedruckt(pbs: Probenbegleitschein) {
        this.api
            .get(
                this.apiBaseUrl + 'probenbegleitschein/gedruckt.php',
                new HttpParams().set('probenbegleitschein_id', pbs.probenbegleitschein_id),
            )
            .subscribe({
                next: () => {
                    this.api.reload('gedruckt');
                    this.showPdf(pbs);
                },
                error: err => {
                    this.messageService.errorMessage('Fehler!', err);
                },
            });
    }

    showPdf(pbs: Probenbegleitschein) {
        const input = new PdfViewerDialogInput(
            'probenbegleitschein/read_or_mail_pdf.php',
            new HttpParams().set('probenbegleitschein_id', pbs?.probenbegleitschein_id?.toString()),
            'Probenbegleitschein',
            { email: { sende_mail: true, kunde_id: pbs?.kunde_id } },
        );
        this.pdfService.openPdfViewer(input).subscribe({
            next: () => {
                this.api.reload();
            },
        });
    }

    duplicate(probenbegleitschein: Probenbegleitschein) {
        this.tableService
            .duplicateEntity(this.apiBaseUrl + 'probenbegleitschein/duplicate.php', probenbegleitschein)
            .subscribe(
                response => {
                    this.messageService.infoMessage('Der Probenbegleitschein wurde erfolgreich dupliziert!');
                    this.router.navigate(['labor', 'probenbegleitscheine', response.probenbegleitschein_id]);
                },
                error => {
                    this.messageService.errorMessage('Der Probenbegleitschein konnte nicht dupliziert werden!', error);
                },
            );
    }

    openDeleteDialog(pbs: Probenbegleitschein) {
        const dialogRef = this.dialog.open(ConfirmDeleteDialogComponent, {
            width: '500px',
            data: {
                title: 'Probenbegleitschein ' + pbs.probenbegleitschein_id + ' wirklich löschen?',
                text: 'Probenbegleitschein ' + pbs.probenbegleitschein_id + ' wirklich löschen?',
            },
        });

        dialogRef.afterClosed().subscribe(response => {
            if (response) {
                this.api.deleteEntity(this.apiBaseUrl + 'probenbegleitschein/delete.php', pbs).subscribe(
                    () => {
                        this.messageService.infoMessage('Der Probenbegleitschein wurde erfolgreich gelöscht!');
                        this.tableService.reload();
                    },
                    error => {
                        this.messageService.errorMessage(
                            'Der Probenbegleitschein konnte nicht gelöscht werden! ',
                            error,
                        );
                    },
                );
            }
        });
    }
}
